import * as SecureStore from "expo-secure-store";
import { Platform } from "react-native";

import { Authorization, AppContextRole, Preferences } from "./types";

export enum StorageKey {
  Authorization = "Authorization",
  Preferences = "Preferences",
}

export interface Storage {
  setItemAsync: (key: string, value: string) => Promise<void>;
  getItemAsync: (key: string) => Promise<string | null>;
  deleteItemAsync: (key: string) => Promise<void>;

  storeAuthorization: (authorization: Authorization) => Promise<void>;
  getAuthorization: () => Promise<Authorization | null>;
  clearAuthorization: () => Promise<void>;

  storePreferences: (preferences: Preferences) => Promise<void>;
  getPreferences: () => Promise<Preferences | null>;
  clearPreferences: () => Promise<void>;
}

class NoopStorage {
  setItemAsync = (key: string, value: string) => {
    return Promise.resolve();
  };

  getItemAsync = (key: string): Promise<string | null> => {
    return Promise.resolve(null);
  };

  deleteItemAsync = (key: string) => {
    return Promise.resolve();
  };

  storeAuthorization = async (authorization: Authorization) => {
    await this.setItemAsync(
      StorageKey.Authorization,
      JSON.stringify(authorization)
    );
  };

  getAuthorization = async (): Promise<Authorization | null> => {
    const maybeAuthorization = await this.getItemAsync(
      StorageKey.Authorization
    );

    if (maybeAuthorization) {
      return JSON.parse(maybeAuthorization);
    }

    return null;
  };

  clearAuthorization = async () => {
    await this.deleteItemAsync(StorageKey.Authorization);
  };

  storePreferences = (preferences: Preferences) =>
    this.setItemAsync(StorageKey.Preferences, JSON.stringify(preferences));

  getPreferences = async (): Promise<Preferences | null> => {
    const maybePreferences = await this.getItemAsync(StorageKey.Preferences);

    if (maybePreferences) {
      return JSON.parse(maybePreferences);
    }

    return null;
  };

  clearPreferences = async () => {
    await this.deleteItemAsync(StorageKey.Preferences);
  };
}

class MobileStorage extends NoopStorage {
  setItemAsync = (key: string, value: string) => {
    return SecureStore.setItemAsync(key, value);
  };

  getItemAsync = (key: string) => {
    return SecureStore.getItemAsync(key);
  };

  deleteItemAsync = (key: string) => {
    return SecureStore.deleteItemAsync(key);
  };
}

class WebStorage extends NoopStorage {
  localStorage;

  constructor() {
    super();
    this.localStorage = window.localStorage;
  }

  setItemAsync = (key: string, value: string) => {
    this.localStorage.setItem(key, value);
    return Promise.resolve();
  };

  getItemAsync = (key: string) => {
    return Promise.resolve(this.localStorage.getItem(key));
  };

  deleteItemAsync = (key: string) => {
    this.localStorage.removeItem(key);
    return Promise.resolve();
  };
}

const storage: Storage = (() => {
  switch (Platform.OS) {
    case "web":
      return new WebStorage();

    case "ios":
    case "android":
      return new MobileStorage();

    default:
      return new NoopStorage();
  }
})();

export default storage;
